import { useEffect, useState } from 'react'
import { cn } from '@/utils/cn'
import {
  subscribeToasts,
  dismiss,
  type ToastItem,
  type ToastType,
} from '@/toast/toastStore'

const TYPE_STYLE: Record<ToastType, { border: string; dot: string; label: string }> = {
  success: { border: 'border-l-v2-success', dot: 'bg-v2-success', label: '成功' },
  error: { border: 'border-l-v2-danger', dot: 'bg-v2-danger', label: 'エラー' },
  warning: { border: 'border-l-v2-warn', dot: 'bg-v2-warn', label: '注意' },
  info: { border: 'border-l-v2-primary', dot: 'bg-v2-primary', label: 'お知らせ' },
}

/**
 * v2 トースト表示コンテナ。App ルートに 1 つだけマウントする。
 * toastStore を購読し、右上にスタック表示する。reduced-motion / a11y 対応。
 */
export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([])

  useEffect(() => subscribeToasts(setItems), [])

  if (items.length === 0) return null

  return (
    <div
      className="app-v2 pointer-events-none fixed right-4 top-4 z-[100] flex w-[min(92vw,22rem)] flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {items.map((t) => {
        const style = TYPE_STYLE[t.type]
        return (
          <div
            key={t.id}
            role={t.type === 'error' || t.type === 'warning' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-v2-md border border-l-4 border-v2-border bg-v2-surface px-4 py-3 shadow-v2-md',
              style.border,
            )}
          >
            <span
              aria-hidden="true"
              className={cn('mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full', style.dot)}
            />
            <div className="min-w-0 flex-1">
              <p className="break-words text-sm font-medium text-v2-ink">
                {t.message}
              </p>
              {t.description ? (
                <p className="mt-0.5 break-words text-xs text-v2-ink-muted">
                  {t.description}
                </p>
              ) : null}
              {t.action ? (
                <button
                  type="button"
                  onClick={() => {
                    t.action?.onClick()
                    dismiss(t.id)
                  }}
                  className="mt-2 rounded-v2-sm bg-v2-primary px-3 py-1 text-xs font-semibold text-white transition-[filter] hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
                >
                  {t.action.label}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="閉じる"
              className="-mr-1 shrink-0 rounded-v2-sm px-1 text-v2-ink-soft transition-colors hover:bg-v2-bg hover:text-v2-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary"
            >
              ×
            </button>
          </div>
        )
      })}
    </div>
  )
}

export default Toaster
